/*
 * @Description:
 * @Date: 2025-07-28 18:05:39
 */
import { WarningFilled } from '@element-plus/icons-vue';
import {
  useTableColumn,
  BaseIcon,
  BasePopover,
  BaseToolTip,
  BaseButton,
  useCreateModal
} from 'xrk-components';
import { formatter } from '@/utils';
import InvoiceSelectDataDialog from '../components/invoice-select-data-dialog/invoice-select-data-dialog.vue';
import { ORDER_TYPE } from '@constants/order';
import { PAY_PASSAGEWAY_MAPPING } from '@constants/bank';
import {
  ALL_INVOICE_STATUS,
  INVOICE_TYPE,
  BLUE_INVOICE_STATUS,
  RED_INVOICE_STATUS
} from '@V/invoice/src/constants';

/** 状态映射对象类型（后端原始值作 key） */
type StatusMap = Record<number, { label: string; value: number }>;

/** 开票状态 */
export const invoiceStatusCol = useTableColumn({
  label: '开票状态',
  prop: 'invoiceStatus',
  width: '180px',
  render: ({ row, cellValue }) => {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center'
        }}
      >
        <span>
          {formatter.defaultFormat(
            cellValue,
            () => ALL_INVOICE_STATUS[cellValue].label
          )}
        </span>
        {/* 已驳回。申请红冲被驳回(驳回的是待红冲，未到红票审核)时，状态回退到已开票(蓝票)的情况下展示驳回原因 */}
        {/* rejectReason对应的是申请开票驳回（-1），蓝红驳回是noPassReason。当-1时订单释放，不会基于当前数据更新，所以优先使用rejectReason。3、7会更新当前数据 */}
        {[-1, 3, 7].includes(cellValue) &&
          (row.rejectReason || row.noPassReason) && (
            <BasePopover
              placement="top"
              width="300px"
              title="驳回原因"
              trigger="hover"
              content={row.rejectReason || row.noPassReason}
              disabled={false}
              v-slots={{
                reference: () => (
                  <BaseIcon>
                    <WarningFilled />
                  </BaseIcon>
                )
              }}
            />
          )}
      </div>
    );
  }
});

/** 发票申请编号 */
export const invoiceOrderNoCol = () =>
  useTableColumn({
    label: '发票申请编号',
    prop: 'invoiceOrderNo',
    width: '180px'
  });

/** 购买方 */
export const enterpriseNameCol = () =>
  useTableColumn({
    label: '购买方',
    prop: 'enterpriseName',
    width: 300
  });

/** 销售方 */
export const supplierNameCol = () =>
  useTableColumn({
    label: '销售方',
    prop: 'supplierName',
    width: 300
  });

/** 订单类型（代办供应商传入 DBGYS_ORDER_TYPE 覆盖） */
export const orderTypeCol = (map: StatusMap = ORDER_TYPE) =>
  useTableColumn({
    label: '订单类型',
    prop: 'orderMixtureType',
    formatter: ({ cellValue }) =>
      formatter.defaultFormat(cellValue, () => map[cellValue]?.label)
  });

/** 发票类型 */
export const invoiceTypeCol = () =>
  useTableColumn({
    label: '发票类型',
    prop: 'invoiceType',
    formatter: ({ cellValue }) =>
      formatter.defaultFormat(cellValue, () => INVOICE_TYPE[cellValue]?.label)
  });

/**
 * 蓝票状态（驳回时 hover 展示驳回原因）
 * @param options 状态映射（平台发票传入 PLATFORM_BLUE_INVOICE_STATUS）
 * @param rejectedStatus 命中则展示驳回 tooltip（平台仅 [3]，个体/代办 [3, 13]）
 */
export const blueInvoiceStatusCol = (
  options: StatusMap = BLUE_INVOICE_STATUS,
  rejectedStatus: number[] = [3, 13]
) =>
  useTableColumn({
    label: '蓝票状态',
    prop: 'blueInvoiceStatus',
    width: '140px',
    render: ({ row, cellValue }) => {
      const label = options[cellValue]?.label || '--';
      const isRejected = rejectedStatus.includes(cellValue);
      const rejectReason = row.noPassReason || row.rejectReason;
      if (isRejected && rejectReason) {
        return (
          <BaseToolTip placement="top" effect="dark" content={rejectReason}>
            <span style="cursor: pointer; border-bottom: 1px dashed #999;">
              {label}
            </span>
          </BaseToolTip>
        );
      }
      return <span>{label}</span>;
    }
  });

/** 红票状态（平台发票传入 PLATFORM_RED_INVOICE_STATUS 覆盖，-- 表示未发起） */
export const redInvoiceStatusCol = (options: StatusMap = RED_INVOICE_STATUS) =>
  useTableColumn({
    label: '红票状态',
    prop: 'redInvoiceStatus',
    width: '140px',
    formatter: ({ cellValue }) => {
      if (cellValue === null || cellValue === undefined) {
        return '--';
      }
      return options[cellValue]?.label || '--';
    }
  });

/** 申请时间 */
export const applyTimeCol = () =>
  useTableColumn({
    label: '申请时间',
    prop: 'createAt',
    formatter: ({ cellValue }) => formatter.date(cellValue),
    width: '180px'
  });

/** 更新时间（列表数据字段为 authAt 审核时间） */
export const updateTimeCol = () =>
  useTableColumn({
    label: '更新时间',
    prop: 'authAt',
    formatter: ({ cellValue }) => formatter.date(cellValue),
    width: '180px'
  });

/* ===== 待开票列表通用列（服务商/供应商/代办供应商） ===== */

/** 购买方 */
export const purchaserNameCol = () =>
  useTableColumn({
    label: '购买方',
    prop: 'purchaserName',
    width: 300
  });

/** 销售方 */
export const theSellerNameCol = () =>
  useTableColumn({
    label: '销售方',
    prop: 'theSellerName',
    width: 300
  });

/** 项目名称 */
export const projectNameCol = () =>
  useTableColumn({
    label: '项目名称',
    prop: 'projectName',
    width: 300
  });

/** 订单类型（来源 @constants/order ORDER_TYPE） */
export const userDefinedOrderTypeCol = () =>
  useTableColumn({
    label: '订单类型',
    prop: 'userDefinedOrderType',
    formatter: ({ cellValue }) =>
      formatter.defaultFormat(cellValue, () => ORDER_TYPE[cellValue]?.label)
  });

/** 收款时间 */
export const receiveAtCol = () =>
  useTableColumn({
    label: '收款时间',
    prop: 'receiveAt',
    width: '180px',
    formatter: ({ cellValue }) => formatter.date(cellValue)
  });

/** 支付通道（来源 @constants/bank PAY_PASSAGEWAY_MAPPING） */
export const payPassagewayCodeCol = () =>
  useTableColumn({
    label: '支付通道',
    prop: 'payPassagewayCode',
    formatter: ({ cellValue }) =>
      formatter.defaultFormat(
        cellValue,
        () => PAY_PASSAGEWAY_MAPPING[cellValue]?.label
      )
  });

/* ===== 工业备案（开票备案）通用列 ===== */

/** 销售方（工业视角，服务商名称） */
export const aspNameCol = () =>
  useTableColumn({
    label: '销售方',
    prop: 'aspName',
    minWidth: 300,
    formatter: ({ cellValue }) => formatter.defaultFormat(cellValue)
  });

/** 纳税人识别号 */
export const invoiceIdentifierNoCol = () =>
  useTableColumn({
    label: '纳税人识别号',
    prop: 'invoiceIdentifierNo',
    minWidth: 200
  });

/** 企业名称（工业备案，服务商视角的工业企业） */
export const industryNameCol = () =>
  useTableColumn({
    label: '企业名称',
    prop: 'industryName',
    minWidth: 300
  });

/** 结算月度 */
export const taskAtCol = () =>
  useTableColumn({
    label: '结算月度',
    prop: 'taskAt',
    formatter: ({ cellValue }) => formatter.date(cellValue, 'YYYY-MM')
  });

/** 发票类目 */
export const itemNameCol = () =>
  useTableColumn({
    label: '发票类目',
    prop: 'itemName'
  });

/** 更新时间（工业备案数据字段为 openAt） */
export const openAtCol = () =>
  useTableColumn({
    label: '更新时间',
    prop: 'openAt',
    minWidth: 180,
    formatter: ({ cellValue }) => formatter.date(cellValue)
  });

/**
 * 开票金额列（票面金额/价格合计）
 * @param label 列标题，默认「开票金额(元)」
 */
export const invoiceAmountCol = (label = '开票金额(元)') =>
  useTableColumn({
    label,
    prop: 'invoiceAmount',
    render({ row }) {
      const amount = formatter.fen2yuan(row.invoiceAmount);
      if (row.invoiceBusinessType === 7) {
        return (
          <BaseButton
            type="text"
            click={() => {
              useCreateModal(InvoiceSelectDataDialog, {
                type: 2,
                invoiceApplyId: row.id,
                invoiceBusinessType: row.invoiceBusinessType
              });
            }}
          >
            {amount}
          </BaseButton>
        );
      }
      return <span>{amount}</span>;
    }
  });

/** 关联结算单号（平台待开票） */
export const settlementNoCol = () =>
  useTableColumn({
    prop: 'settlementNo',
    label: '关联结算单号'
  });

/** 关联项目服务费订单号（平台待开票） */
export const projectServiceFeeOrderNoCol = () =>
  useTableColumn({
    prop: 'projectServiceFeeOrderNo',
    label: '关联项目服务费订单号'
  });

/** 订单编号（纯文本，待开票列表用） */
export const orderNoCol = () =>
  useTableColumn({
    prop: 'userDefinedOrderNo',
    label: '订单编号',
    width: '260px'
  });
